import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { DataTable } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTable';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Communicator } from '../../tools/Communication';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { ContainerLocationStatus } from '@/utils/containerLocation';
import {
  getCheckoutsByUserColumns,
  UserHolding,
} from './checkoutsByUserColumns';

const CHECKOUTS_PAGE_SIZE = 20;

const CheckoutsByUserPage: React.FC = () => {
  const location = useLocation();
  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);

  const params = new URLSearchParams(location.search);
  const externalUserId = params.get('externalUserId') ?? '';
  const storeId = params.get('storeId') ?? '';
  // Exact transactions URL the user came from; fall back to the store page.
  const backTo = params.get('from') || Routes.partnerMerchants();

  const [holdings, setHoldings] = useState<UserHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  // The holding awaiting return confirmation (drives the confirm dialog).
  const [holdingToReturn, setHoldingToReturn] = useState<UserHolding | null>(
    null,
  );
  // Id of the holding whose return request is in flight (drives the row spinner).
  const [returningId, setReturningId] = useState<number | null>(null);
  // Same pair for the "don't charge" (exempt from invoice) flow.
  const [holdingToExempt, setHoldingToExempt] = useState<UserHolding | null>(
    null,
  );
  const [exemptingId, setExemptingId] = useState<number | null>(null);

  useBreadcrumbs({
    id: 'checkouts-by-user',
    items: [
      {
        label: i18n.t('Common:transactions'),
        href: backTo,
      },
      {
        label: i18n.t('MerchantTransactions:checkoutsByUserTitle', {
          name: externalUserId,
        }),
        isCurrent: true,
      },
    ],
  });

  const loadHoldings = useCallback(() => {
    setLoading(true);
    setHasError(false);

    Communicator.PostData('api/3/Statistics/GetCurrentHoldingsForUser', {
      externalIntegrationUserId: externalUserId,
      storeId: storeId || null,
    })
      .then((response) => response.json())
      .then((data) => {
        setHoldings(data.holdings ?? []);
        setLoading(false);
      })
      .catch(() => {
        setHasError(true);
        setLoading(false);
      });
  }, [externalUserId, storeId]);

  useEffect(() => {
    loadHoldings();
  }, [loadHoldings]);

  const handleReturn = useCallback((holding: UserHolding) => {
    setHoldingToReturn(holding);
  }, []);

  const handleDontCharge = useCallback((holding: UserHolding) => {
    setHoldingToExempt(holding);
  }, []);

  // Runs when the user confirms the dialog: hands the container back to the
  // store via the same endpoint the Partner App uses, then reloads the list.
  const handleReturnConfirmed = () => {
    const holding = holdingToReturn;
    if (!holding) {
      return;
    }
    setHoldingToReturn(null);
    setReturningId(holding.id);

    Communicator.PostData('api/3/Container/ContainerReturn', {
      codes: [`c${holding.containerId}`],
    })
      .then(() => {
        toast.success(
          i18n.t('MerchantTransactions:returnSuccess', {
            name: holding.containerName,
          }),
        );
        loadHoldings();
      })
      .catch(() => {
        toast.error(
          i18n.t('MerchantTransactions:returnError', {
            name: holding.containerName,
          }),
        );
      })
      .finally(() => {
        setReturningId(null);
      });
  };

  // Runs when the user confirms the don't-charge dialog: flips the holding to
  // OverdueNoCharge so it falls off the upcoming partner invoice, then reloads.
  const handleDontChargeConfirmed = () => {
    const holding = holdingToExempt;
    if (!holding) {
      return;
    }
    setHoldingToExempt(null);
    setExemptingId(holding.id);

    Communicator.PostData('api/3/Statistics/ExemptHoldingFromCharge', {
      containerLocationId: holding.id,
      storeId: storeId || null,
    })
      .then(() => {
        toast.success(
          i18n.t('MerchantTransactions:dontChargeSuccess', {
            name: holding.containerName,
          }),
        );
        loadHoldings();
      })
      .catch(async (error) => {
        // The backend rejects with a reason (e.g. charge is from a previous
        // month); surface it when available.
        const body =
          error instanceof Response
            ? await error.json().catch(() => null)
            : null;
        toast.error(
          body?.errorMessage ??
            i18n.t('MerchantTransactions:dontChargeError', {
              name: holding.containerName,
            }),
        );
      })
      .finally(() => {
        setExemptingId(null);
      });
  };

  // Split the open holdings into the two lists: holdings already charged to
  // the partner in the current calendar month go to "on the upcoming invoice"
  // (the charge is on it unless exempted or returned before the invoice run);
  // everything else stays "with the user". OverdueError is hidden on purpose.
  const { holdingsWithUser, holdingsOnInvoice } = useMemo(() => {
    const now = new Date();
    const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
    const visible = holdings.filter(
      (h) => h.status !== ContainerLocationStatus.OverdueError,
    );
    const onInvoice = visible.filter(
      (h) =>
        h.status === ContainerLocationStatus.OverdueSold &&
        h.chargedTimestamp != null &&
        new Date(h.chargedTimestamp).getTime() >= monthStart,
    );
    const onInvoiceIds = new Set(onInvoice.map((h) => h.id));
    return {
      holdingsWithUser: visible.filter((h) => !onInvoiceIds.has(h.id)),
      holdingsOnInvoice: onInvoice,
    };
  }, [holdings]);

  const withUserColumns = useMemo(
    () =>
      getCheckoutsByUserColumns({
        canAccessPartnerData,
        onReturn: handleReturn,
        returningId,
        variant: 'withUser',
      }),
    [canAccessPartnerData, handleReturn, returningId],
  );

  const onInvoiceColumns = useMemo(
    () =>
      getCheckoutsByUserColumns({
        canAccessPartnerData,
        onReturn: handleReturn,
        returningId,
        variant: 'onInvoice',
        onDontCharge: handleDontCharge,
        dontChargingId: exemptingId,
      }),
    [
      canAccessPartnerData,
      handleReturn,
      returningId,
      handleDontCharge,
      exemptingId,
    ],
  );

  return (
    <div className="tw:mx-auto tw:flex tw:max-w-[1340px] tw:flex-col tw:gap-8 tw:p-6">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <h2 className="tw:text-2xl tw:font-semibold">
          {i18n.t('MerchantTransactions:withUserListTitle')}
        </h2>
        <DataTable
          columns={withUserColumns}
          data={holdingsWithUser}
          isLoading={loading}
          error={hasError ? i18n.t('Common:error') : null}
          opts={{
            pageSize: CHECKOUTS_PAGE_SIZE,
            paginationVariant: 'numbered',
            viewOptionsVariant: 'icon',
          }}
        />
      </div>

      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <h2 className="tw:text-2xl tw:font-semibold">
            {i18n.t('MerchantTransactions:onInvoiceListTitle')}
          </h2>
          <p className="tw:text-muted-foreground tw:text-sm">
            {i18n.t('MerchantTransactions:onInvoiceListDescription')}
          </p>
        </div>
        <DataTable
          columns={onInvoiceColumns}
          data={holdingsOnInvoice}
          isLoading={loading}
          error={hasError ? i18n.t('Common:error') : null}
          opts={{
            pageSize: CHECKOUTS_PAGE_SIZE,
            paginationVariant: 'numbered',
            viewOptionsVariant: 'icon',
          }}
        />
      </div>

      <AlertDialog
        open={!!holdingToReturn}
        onOpenChange={(open) => {
          if (!open) {
            setHoldingToReturn(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {i18n.t('MerchantTransactions:returnConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {i18n.t('MerchantTransactions:returnConfirmDescription', {
                name: holdingToReturn?.containerName ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n.t('Common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReturnConfirmed}>
              {i18n.t('MerchantTransactions:returnConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!holdingToExempt}
        onOpenChange={(open) => {
          if (!open) {
            setHoldingToExempt(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {i18n.t('MerchantTransactions:dontChargeConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {i18n.t('MerchantTransactions:dontChargeConfirmDescription', {
                name: holdingToExempt?.containerName ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n.t('Common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDontChargeConfirmed}>
              {i18n.t('MerchantTransactions:dontChargeConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CheckoutsByUserPage;
